import { PrivateRoute } from "@src/components/routing";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Redirect, Route, Switch } from "react-router-dom";

import { useAdminMenuPriviligesProviderContext } from "../priviligesProviders/AdminMenuPriviligesProvider";
import { LegacyRouteWrapper } from "./LegacyRouteWrapper";

const Accounts = loadLazily(async () => await import("@src/containers/accountManagement"));

const ShiftSearchPage = loadLazily(
  async () =>
    await import("@src/containers/adminShifts/ShiftSearchPage").then((module) => ({
      default: module.ShiftSearchPage,
    }))
);

const Logs = loadLazily(
  async () => await import("@src/containers/logs").then((module) => ({ default: module.Logs }))
);

const TimeclockRequests = loadLazily(
  async () =>
    await import("@src/containers/timeclockrequests").then((module) => ({
      default: module.TimeclockRequests,
    }))
);

const UrgentShiftsNoShowCases = loadLazily(
  async () =>
    await import("@src/containers/urgentShiftsNoShowCases").then((module) => ({
      default: module.UrgentShiftsNoShowCases,
    }))
);

const ProofOfEarnings = loadLazily(async () => await import("@src/containers/proofOfEarnings"));

const PayRates = loadLazily(
  async () =>
    await import("@src/containers/shiftPayments/payRates").then((module) => ({
      default: module.PayRates,
    }))
);

const AdminToolsTimekeepingVideosPage = loadLazily(
  async () =>
    await import("@src/appV2/AdminTools/Page").then((module) => ({
      default: module.AdminToolsPage,
    }))
);

const AdminToolsShiftExplainerPage = loadLazily(
  async () =>
    await import("@src/appV2/AdminTools/ShiftExplainer/components/Page").then((module) => ({
      default: module.ShiftExplainerPage,
    }))
);

const AdminToolsJobsScraperPage = loadLazily(
  async () =>
    await import("@src/appV2/AdminTools/JobsScraper/Page").then((module) => ({
      default: module.JobsScraperPage,
    }))
);

const ManageQualifications = loadLazily(
  async () =>
    await import("@src/appV2/Admin").then((module) => ({
      default: module.ManageQualificationsPage,
    }))
);

const ManageWorkplaceTypes = loadLazily(
  async () =>
    await import("@src/appV2/Admin").then((module) => ({
      default: module.ManageWorkplaceTypesPage,
    }))
);

const ManageSkillsAssessments = loadLazily(
  async () =>
    await import("@src/appV2/Admin").then((module) => ({
      default: module.ManageSkillsAssessmentsPage,
    }))
);

const AdminPlacementsAvailabilityPage = loadLazily(
  async () =>
    await import("@src/appV2/AdminPlacements/Availability/Page").then((module) => ({
      default: module.AdminPlacementsAvailabilityPage,
    }))
);

const AdminQRCode = loadLazily(
  async () =>
    await import("@src/appV2/QRCode/QRCodePage").then((module) => ({
      default: module.QRCodePage,
    }))
);

const EditProfile = loadLazily(async () => await import("@src/containers/editProfile"));

const WorkerDocuments = loadLazily(
  async () =>
    await import("@src/appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
      default: module.WorkerDocumentsView,
    }))
);

const InvoiceView = loadLazily(
  async () =>
    await import("@src/components/invoiceView").then((module) => ({
      default: module.InvoiceView,
    }))
);

const PDFGenerator = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/pdfGenerator").then((module) => ({
      default: module.PDFGenerator,
    }))
);

const AdminPostInvoiceDisputes = loadLazily(
  async () =>
    await import(
      "@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPostInvoiceDisputes"
    ).then((module) => ({
      default: module.AdminPostInvoiceDisputes,
    }))
);

const AdminPreInvoiceDisputes = loadLazily(
  async () =>
    await import(
      "@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputes"
    ).then((module) => ({
      default: module.AdminPreInvoiceDisputes,
    }))
);

const AdminDisputePage = loadLazily(
  async () =>
    await import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminDisputePage").then(
      (module) => ({
        default: module.AdminDisputePage,
      })
    )
);

const AdminPreInvoiceDisputeResolution = loadLazily(
  async () =>
    await import(
      "@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputeResolution"
    ).then((module) => ({
      default: module.AdminPreInvoiceDisputeResolution,
    }))
);

const TimesheetView = loadLazily(
  async () =>
    await import("@src/containers/accountManagement/invoice/timesheetView").then((module) => ({
      default: module.TimesheetView,
    }))
);

export function AdminRoutes() {
  const adminMenuPriviliges = useAdminMenuPriviligesProviderContext();

  return (
    <Switch>
      {/* Redesigned components without Legacy Wrapper */}

      {/* Legacy components with Legacy Wrapper */}
      <LegacyRouteWrapper>
        <PrivateRoute
          path={routes.adminAccounts}
          component={Accounts}
          isAuthorized={adminMenuPriviliges.accountsSection}
        />

        <PrivateRoute
          path={routes.adminShifts}
          component={ShiftSearchPage}
          isAuthorized={adminMenuPriviliges.shiftsSection?.shiftSearch}
        />

        <PrivateRoute
          path={routes.adminLogs}
          component={Logs}
          isAuthorized={adminMenuPriviliges.shiftsSection?.shiftLogs}
        />

        <PrivateRoute
          path={routes.adminTimeclock}
          component={TimeclockRequests}
          isAuthorized={adminMenuPriviliges.shiftsSection?.timeclockRequests}
        />

        <PrivateRoute
          path={routes.adminUrgentShiftNcns}
          component={UrgentShiftsNoShowCases}
          isAuthorized={adminMenuPriviliges.shiftsSection?.noShows}
        />

        <PrivateRoute
          path={routes.agentProof}
          component={ProofOfEarnings}
          isAuthorized={adminMenuPriviliges.payrollSection?.proofOfEarning}
        />

        <PrivateRoute
          exact
          path={routes.adminShiftPaymentsPayRates}
          component={PayRates}
          isAuthorized={adminMenuPriviliges.payrollSection?.shiftPayments}
        />

        <PrivateRoute
          path={routes.adminToolsTimekeepingVideos}
          component={AdminToolsTimekeepingVideosPage}
          isAuthorized={adminMenuPriviliges.adminToolsSection?.timekeepingVideos}
        />

        <PrivateRoute
          path={routes.adminToolsShiftExplainer}
          component={AdminToolsShiftExplainerPage}
          isAuthorized={adminMenuPriviliges.adminToolsSection?.shiftExplainer}
        />

        <PrivateRoute
          path={routes.adminToolsJobsScraper}
          component={AdminToolsJobsScraperPage}
          isAuthorized={adminMenuPriviliges.adminToolsSection?.jobsScraper}
        />

        <PrivateRoute
          exact
          path={routes.adminManageQualifications}
          component={ManageQualifications}
          isAuthorized={adminMenuPriviliges.staffingSection?.qualifications}
        />

        <PrivateRoute
          exact
          path={routes.adminManageQualifications}
          component={ManageQualifications}
          isAuthorized={adminMenuPriviliges.staffingSection?.qualifications}
        />

        <PrivateRoute
          exact
          path={routes.adminManageWorkplaceTypes}
          component={ManageWorkplaceTypes}
          isAuthorized={adminMenuPriviliges.staffingSection?.workplaceTypes}
        />

        <PrivateRoute
          exact
          path={routes.adminManageSkillsAssessments}
          component={ManageSkillsAssessments}
          isAuthorized={adminMenuPriviliges.staffingSection?.skillAssessments}
        />

        <PrivateRoute
          exact
          path={routes.adminPlacementsAvailability}
          component={AdminPlacementsAvailabilityPage}
          isAuthorized={adminMenuPriviliges.hiringSection?.availability}
        />

        <PrivateRoute exact path={routes.adminQRCode} component={AdminQRCode} isAuthorized={true} />

        <PrivateRoute
          exact
          path={routes.adminProfile}
          component={EditProfile}
          isAuthorized={adminMenuPriviliges.routeAuth?.isAdmin}
        />

        <PrivateRoute
          exact
          path={routes.adminDocument}
          component={WorkerDocuments}
          isAuthorized={adminMenuPriviliges.accountsSection?.requirements}
        />

        <PrivateRoute
          exact
          path={routes.adminInvoice}
          component={InvoiceView}
          isAuthorized={adminMenuPriviliges.routeAuth?.isAdmin}
        />

        <PrivateRoute
          exact
          path={routes.adminInvoicePdf}
          component={PDFGenerator}
          isAuthorized={adminMenuPriviliges.routeAuth?.isAdmin}
        />

        <PrivateRoute
          exact
          path={routes.adminAccountsPostInvoiceDisputes}
          component={AdminPostInvoiceDisputes}
          isAuthorized={adminMenuPriviliges.routeAuth?.hasBillingOrAdministrativeRole}
        />

        <PrivateRoute
          exact
          path={routes.adminAccountsPreInvoiceDisputes}
          component={AdminPreInvoiceDisputes}
          isAuthorized={adminMenuPriviliges.routeAuth?.hasBillingOrAdministrativeRole}
        />

        <PrivateRoute
          exact
          path={routes.adminInvoiceDispute}
          component={AdminDisputePage}
          isAuthorized={adminMenuPriviliges.routeAuth?.hasBillingOrAdministrativeRole}
        />

        <PrivateRoute
          exact
          path={routes.adminPreInvoiceDispute}
          component={AdminPreInvoiceDisputeResolution}
          isAuthorized={adminMenuPriviliges.routeAuth?.hasBillingOrAdministrativeRole}
        />

        <Route exact path={routes.timesheet} component={TimesheetView} />

        <PrivateRoute
          exact
          path={routes.timesheetPdf}
          component={PDFGenerator}
          isAuthorized={adminMenuPriviliges.routeAuth?.isAdmin}
        />
      </LegacyRouteWrapper>

      {/* Catch-all: redirect unrecognized admin routes to the default admin page */}
      <Route>
        <Redirect to={routes.adminAccountsAgents} />
      </Route>
    </Switch>
  );
}
